// @flow
import type { AddOn, JournalEntry, LedgerAccount, TransactionCategorization } from './types';
import { compareAllDataEntries } from './comparators';

const awaitingCategoryRevenueCode = '4500';
const awaitingCategoryExpenseCode = '5680';
const awaitingCategoryLedgers = {
  awaitingCategoryExpense: {
    code: '',
    dataEntries: [],
    details: '',
    id: '',
    internalNotes: [],
    name: '',
  },
  awaitingCategoryRevenue: {
    code: '',
    dataEntries: [],
    details: '',
    id: '',
    internalNotes: [],
    name: '',
  },
};

const compileCategorizationViewData = (
  addOns: Array<AddOn>,
  categorizationInconsistencies: Array<Object>,
  dataEntries: Array<Object>,
  journalEntries: Array<JournalEntry>,
  ledgerAccounts: Array<LedgerAccount>,
  transactionCategorizations: Array<TransactionCategorization>,
) => {
  const inconsistentDataEntryIdSet = categorizationInconsistencies.reduce(
    (allInconsistentDataEntryIds, { dataEntryIds }) => {
      // Each categorization inconsistency has a list of data entry IDs that should be marked as
      // being inconsistently categorized, so put them all in one set to check against
      dataEntryIds.forEach(id => allInconsistentDataEntryIds.add(id));
      return allInconsistentDataEntryIds;
    },
    new Set(),
  );

  const addOnsMap: Map<string, AddOn> = new Map();
  if (addOns) {
    addOns.forEach(addOn => addOnsMap.set(addOn.id, addOn));
  }

  const ledgerAccountMap: Map<string, LedgerAccount> = new Map();
  if (ledgerAccounts) {
    ledgerAccounts.forEach(ledgerAccount => {
      const ledgerAccountWithDataEntries = {
        ...ledgerAccount,
        dataEntries: [],
      };

      ledgerAccountMap.set(ledgerAccount.id, ledgerAccountWithDataEntries);

      if (ledgerAccount.code === awaitingCategoryExpenseCode) {
        awaitingCategoryLedgers.awaitingCategoryExpense = ledgerAccountWithDataEntries;
      }

      if (ledgerAccount.code === awaitingCategoryRevenueCode) {
        awaitingCategoryLedgers.awaitingCategoryRevenue = ledgerAccountWithDataEntries;
      }
    });
  }

  const transactionCategorizationsMap: Map<string, TransactionCategorization> = new Map();
  if (transactionCategorizations) {
    transactionCategorizations.forEach(categorization => {
      const suggestedLedgers = categorization.suggestedLedgers.map(suggestedLedger => ({
        ...suggestedLedger,
        ledgerAccount: ledgerAccountMap.get(suggestedLedger.ledgerAccount.id),
      }));
      const categorizationWithSuggestedLedger = {
        ...categorization,
        suggestedLedgers,
      };
      transactionCategorizationsMap.set(categorization.id, categorizationWithSuggestedLedger);
    });
  }

  const journalEntriesMap: Map<string, JournalEntry> = new Map();
  if (journalEntries) {
    journalEntries.forEach(originalJournalEntry => {
      const addOn = originalJournalEntry.addOn
        ? addOnsMap.get(originalJournalEntry.addOn.id)
        : undefined;
      const categorization = originalJournalEntry.categorization
        ? transactionCategorizationsMap.get(originalJournalEntry.categorization.id)
        : undefined;
      const journalEntry = {
        ...originalJournalEntry,
        addOn,
        categorization,
      };

      journalEntriesMap.set(originalJournalEntry.id, journalEntry);
    });
  }
  if (dataEntries) {
    dataEntries.forEach(originalDataEntry => {
      const ledgerAccount = ledgerAccountMap.get(originalDataEntry.ledgerAccount.id) || {};
      const isLedgerInAwaitingCategory =
        ledgerAccount.code === awaitingCategoryExpenseCode ||
        ledgerAccount.code === awaitingCategoryRevenueCode;

      if (!isLedgerInAwaitingCategory) {
        return;
      }

      const journalEntry = journalEntriesMap.get(originalDataEntry.journalEntry.id);
      const hasPotentialQualityIssues = inconsistentDataEntryIdSet.has(originalDataEntry.id);
      const dataEntry = {
        ...originalDataEntry,
        hasPotentialQualityIssues,
        journalEntry,
        ledgerAccount,
      };

      ledgerAccount.dataEntries.push(dataEntry);
    });
  }

  awaitingCategoryLedgers.awaitingCategoryExpense.dataEntries.sort(compareAllDataEntries);
  awaitingCategoryLedgers.awaitingCategoryRevenue.dataEntries.sort(compareAllDataEntries);

  return awaitingCategoryLedgers;
};

export default compileCategorizationViewData;
